// ============================================================
// Moteur PUR du compteur d'heures (banque d'heures / annualisation).
// AUCUNE dépendance Angular. Testé sans TestBed : hr-counter.spec.ts.
// ============================================================
//
// « Combien d'heures il a en stock ? » — la question que pose tout gérant qui fait récupérer
// des heures plutôt que de les payer. Le compteur est un SOLDE CUMULÉ, reporté de période en
// période depuis une date d'initialisation.
//
// La décomposition reprend celle observée chez Skello, ligne pour ligne :
//
//     heures travaillées & absences planning
//   − absences NON incluses dans le compteur      (c_values 23, data.counted = false)
//   − volume horaire de référence (contrat)       (modulation : cycle_hours si défini)
//   + heures en plus avec majoration
//   ─────────────────────────────────────────
//   = compteur AVANT modification manuelle
//   + modifications manuelles                     (table hr_counter)
//   ─────────────────────────────────────────
//   = compteur APRÈS modification manuelle        → reporté sur la période suivante
//
// Le solde n'est JAMAIS stocké : il se recalcule intégralement. Seules les corrections
// humaines vivent en base, ce qui les rend traçables et réversibles.

import { C_value } from '@app/shared/_models/common';
import { HrAbsence, HrDayValidation, HrEmployee, hrCounterVoletOf } from '@app/shared/_models/hr';
import { HR_ABSENCE_STATE_APPROVED, HR_COUNTER_KIND_MANUAL } from '@app/shared/constants';
import { EffectiveSlot, addDays, elapsedPlannedMinutes, isoWeekOf, isoWeekdayOf, mondayOf, plannedMinutes, weekIndexOf } from './hr-schedule-engine';
import { HrTimeSource } from './hr-payroll';
import {
	EmployeeWorkProfile, HR_WORKTIME_RULES_DEFAULT, OvertimeEntry, PremiumSlot, WorkTimeRules,
	effectiveWorkRules, premiumRestMinutesOf, premiumTotalsOf, rcrBalanceOf, splitDestinations, weekOutcome,
} from './hr-worktime';
import { hrCountedLeaveDays } from './hr-leave';
import { HolidayRules, closedHolidaysInRange, holidayMapOf, holidayOf, holidayPremiumOf } from './hr-holidays';
import { WorkPeriod, retainedDayMinutes } from './hr-timesheet';

export type CounterGranularity = 'week' | 'month' | 'year';

export interface CounterCell {
	key: string;										// '2026', '2026-07' ou '2026-W30'
	label: string;									// 'S30', 'juil.', '2026'
	from: string;
	to: string;
	worked_min: number;							// heures travaillées & absences COMPTÉES comme du travail
	assimilated_min: number;				// heures de congés payés ASSIMILÉES (neutralisent la référence)
	basis_min: number;							// = worked + assimilé — l'assiette, celle qui déclenche les majorations
	excluded_absence_min: number;		// heures d'absence non créditées (congés payés compris)
	contract_min: number;						// − volume de référence de la période
	extra_min: number;							// + heures au-delà du seuil (déjà comprises dans basis_min)
	raw_balance: number;						// = variation avant modification manuelle
	manual_min: number;							// modifications manuelles imputées sur la période
	balance: number;								// = variation après modification manuelle
	cumulative: number;							// SOLDE CUMULÉ depuis l'initialisation — la colonne utile
	planned: boolean;								// la période porte-t-elle un planning ? (sinon « non planifié »)
	// La période contient une semaine NON TERMINÉE : son travail est affiché, mais elle ne solde
	// rien tant qu'elle n'est pas close (cf. buildCounter). Sans ce drapeau, l'écran laisserait
	// croire que la semaine en cours « ne compte pas », alors qu'elle compte plus tard.
	pending: boolean;
	// Volet RÉCUPÉRATION — distinct du solde d'écart. TROIS origines, un seul stock à poser :
	//   `rcr_credited_min`      heures supplémentaires remplacées par un repos, majoration comprise
	//                           (1 h à +25 % ⇒ 1 h 15) ;
	//   `premium_rest_min`      repos ouvert par une majoration — le dimanche ou le férié travaillé
	//                           qui donne « x % du temps en récupération » en plus du paiement ;
	//   `cor_min`               contrepartie obligatoire en repos, due au-delà du contingent annuel
	//                           (L3121-30). Elle ne remplace aucun paiement : elle s'y ajoute.
	// Le SOLDE les additionne, parce que du point de vue du salarié c'est le même compteur — mais
	// chaque origine reste lisible, parce qu'elles n'obéissent pas aux mêmes délais de prise.
	rcr_credited_min: number;
	premium_rest_min: number;
	cor_min: number;
	rcr_taken_min: number;
	rcr_balance_min: number;
}

const MONTH_LABELS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

// Cellule NEUTRE — pour les personnes dont les heures ne se comptent pas. Tout à zéro et
// `planned: false` : l'écran affiche des tirets, exactement comme une période sans planning.
function emptyCounterCell(bucket: { key: string; label: string; from: string; to: string }): CounterCell {
	return {
		...bucket,
		worked_min: 0, assimilated_min: 0, basis_min: 0, excluded_absence_min: 0,
		contract_min: 0, extra_min: 0,
		raw_balance: 0, manual_min: 0, balance: 0, cumulative: 0,
		planned: false, pending: false,
		rcr_credited_min: 0, premium_rest_min: 0, cor_min: 0, rcr_taken_min: 0, rcr_balance_min: 0,
	};
}

// Volume hebdomadaire de RÉFÉRENCE pour la semaine du `monday`.
// Modulation : si `cycle_hours` est défini sur la fiche, la semaine du cycle donne son volume —
// une semaine à 34 h dans un cycle 34/35/36 n'est PAS un déficit d'une heure.
export function referenceWeekMinutes(
	emp: HrEmployee,
	monday: string,
	anchorMonday: string | null,
	cycleWeeks: number,
): number {
	const cycle = emp.cycle_hours ?? null;
	if (cycle && cycle.length > 0 && anchorMonday && cycleWeeks > 1) {
		const wi = weekIndexOf(monday, anchorMonday, cycleWeeks);
		const h = cycle[wi % cycle.length];
		if (h != null && !isNaN(+h)) return Math.round(+h * 60);
	}
	return emp.contract_hours_week != null ? Math.round(+emp.contract_hours_week * 60) : 0;
}

// Découpe [from, to] en périodes selon la granularité demandée.
function bucketsOf(from: string, to: string, granularity: CounterGranularity): { key: string; label: string; from: string; to: string }[] {
	const out: { key: string; label: string; from: string; to: string }[] = [];
	if (!from || !to || to < from) return out;

	if (granularity === 'week') {
		for (let d = mondayOf(from); d <= to; d = addDays(d, 7)) {
			out.push({ key: d, label: 'S' + isoWeekOf(d), from: d, to: addDays(d, 6) });
		}
		return out;
	}

	if (granularity === 'month') {
		let cur = from.slice(0, 8) + '01';
		while (cur <= to) {
			const next = addDays(cur, 32).slice(0, 8) + '01';
			out.push({ key: cur.slice(0, 7), label: MONTH_LABELS[+cur.slice(5, 7) - 1], from: cur, to: addDays(next, -1) });
			cur = next;
		}
		return out;
	}

	for (let y = +from.slice(0, 4); y <= +to.slice(0, 4); y++) {
		out.push({ key: '' + y, label: '' + y, from: y + '-01-01', to: y + '-12-31' });
	}
	return out;
}

export interface CounterInput {
	employee: HrEmployee;
	slots: EffectiveSlot[];					// projection effective sur toute la plage
	periods: WorkPeriod[];					// pointages appairés
	absences: HrAbsence[];
	// Deux drapeaux sur le type d'absence (c_values 23, champ `data`) :
	//   `counted: true`        — les heures comptent comme du TRAVAIL (elles créditent le compteur) ;
	//   `assimilated: false`   — les heures ne neutralisent PAS la référence (congé sans solde :
	//                            la semaine est réellement plus courte, le déficit est légitime).
	// Par défaut une absence est neutre : elle ne crédite rien et ne creuse rien (congés payés).
	absenceTypes: C_value[];
	// Fériés chômés (frenchHolidaysOf) : ils ne se décomptent pas des congés, donc ne valorisent
	// rien. Absents ⇒ tous les jours de la plage d'absence comptent.
	holidays?: Map<string, string>;
	// Calendrier COMPLET avec les décisions d'ouverture (c_values 25). Fourni, il permet de
	// valoriser les fériés chômés ; absent, les fériés ne sont que « non décomptés des congés ».
	holidayRules?: HolidayRules | null;
	// Mouvements de compteur saisis (table hr_counter). `kind` décide du VOLET alimenté — écart ou
	// récupération — via `hrCounterVoletOf` : aucune nature n'est câblée en dur ici, et une nature
	// ajoutée dans constants.toml traverse la chaîne sans toucher à ce moteur.
	adjustments: { dat: string; minutes: number; kind?: number }[];
	anchorMonday: string | null;		// ancre du cycle de son emploi du temps par défaut
	cycleWeeks: number;
	from: string;
	to: string;
	granularity: CounterGranularity;
	source?: HrTimeSource;					// qui compte les heures (règles de paie) — défaut : le planning
	// Journées ARBITRÉES par un humain (clôture du responsable, déclaration du salarié) : elles
	// l'emportent sur le planning comme sur le badge, exactement comme en paie.
	validations?: HrDayValidation[];
	// Le compteur d'une ANNÉE ENTIÈRE se calcule en juillet : sans ces deux bornes, les mois à
	// venir seraient comptés comme travaillés et le solde de décembre serait déjà « acquis ».
	// `today` absent = aucun bornage (comportement historique).
	today?: string;
	now_min?: number;								// minute courante du jour (Europe/Paris)
	rules?: WorkTimeRules;					// règles légales/conventionnelles — défaut : le régime supplétif
	profile?: EmployeeWorkProfile | null;			// surcharges de la fiche salarié
	overtimeEntries?: OvertimeEntry[];				// arbitrages payé/récupéré déjà décidés
	rcrTaken?: { dat: string; minutes: number }[];		// repos compensateurs effectivement PRIS
	// HEURES MISES EN RÉCUPÉRATION dans le rapport de paie, clé « id_employee|lundi|taux|nature ».
	// C'est ce qui fait que le compteur du salarié et le récapitulatif du mois disent la MÊME
	// chose : sans elle, la fiche afficherait la politique de l'établissement pendant que la paie
	// applique la décision du responsable RH.
	restDecided?: Map<string, number>;
}

/// Construit le tableau du compteur, période par période, avec le CUMUL reporté.
export function buildCounter(input: CounterInput): CounterCell[] {
	const { employee, slots, periods, absences, absenceTypes, adjustments, anchorMonday, cycleWeeks, from, to, granularity } = input;
	const source: HrTimeSource = input.source ?? 'planning';
	const rules = input.rules ?? HR_WORKTIME_RULES_DEFAULT;
	const profile = input.profile ?? null;
	// Deux usages distincts du calendrier : la MAP sert à ne pas décompter un férié des congés,
	// les RÈGLES disent en plus si l'établissement ouvre — donc si la journée est maintenue.
	const holidayRules = input.holidayRules ?? null;
	const holidays = input.holidays ?? (holidayRules ? holidayMapOf(holidayRules) : new Map<string, string>());
	const today = input.today ?? null;
	const nowMin = input.now_min ?? 24 * 60;
	const myValidations = (input.validations ?? []).filter(v => v.id_employee === employee.id);

	const approved = absences.filter(a => a.state === HR_ABSENCE_STATE_APPROVED && a.id_employee === employee.id);
	// Types dont les heures COMPTENT comme du travail (elles créditent le compteur).
	const countedTypes = new Set(absenceTypes.filter(t => t.data?.counted === true).map(t => t.id));
	// Types dont les heures ne neutralisent PAS la référence : congé sans solde, absence
	// injustifiée… La semaine est réellement plus courte, et le déficit est mérité.
	const notAssimilatedTypes = new Set(absenceTypes.filter(t => t.data?.counted !== true && t.data?.assimilated === false).map(t => t.id));

	// Le type d'absence d'un créneau effacé, retrouvé par la demande qui le couvre.
	const absenceTypOf = (dat: string): number | null => approved.find(a => HrAbsence.covers(a, dat))?.typ ?? null;

	// HORS DÉCOMPTE et FORFAIT JOURS : il n'y a pas de compteur d'heures à tenir. Le cadre dirigeant
	// et le gérant non salarié sont hors du titre de la durée du travail ; le forfait jours décompte
	// des journées, si bien qu'un « écart au contrat » en heures n'y veut rien dire.
	// On rend des cellules VIDES plutôt qu'aucune cellule : l'écran garde sa grille de périodes et
	// affiche des tirets, au lieu de laisser croire à un chargement qui n'a pas abouti.
	const resolvedProfile = effectiveWorkRules(null, rules, profile);
	if (!resolvedProfile.counts_hours) {
		return bucketsOf(from, to, granularity).map(bucket => emptyCounterCell(bucket));
	}

	// La règle générale « jour férié » des majorations : elle sert de repli aux jours qui n'ont pas
	// de taux propre dans le calendrier de l'établissement.
	const holidayRule = rules.premiums.find(p => p.kind === 'holiday');

	let cumulative = 0;
	// CONTINGENT ANNUEL consommé depuis le début de la plage. Il court d'un bucket à l'autre : la
	// contrepartie obligatoire en repos naît des heures qui FRANCHISSENT le contingent, et savoir
	// lesquelles suppose de les compter dans l'ordre où elles ont été faites.
	let contingentUsed = 0;
	return bucketsOf(from, to, granularity).map(bucket => {
		// La SEMAINE est indivisible : le volume de référence est hebdomadaire, on ne peut pas
		// compter une semaine entière de contrat face à trois jours de travail. Une semaine
		// appartient donc au bucket qui contient son LUNDI, travail ET contrat ensemble — même
		// principe que le rattachement des heures sup dans hr-payroll.ts.
		const weekMondays: string[] = [];
		for (let d = mondayOf(bucket.from); d <= bucket.to; d = addDays(d, 7)) {
			if (d >= bucket.from) weekMondays.push(d);
		}
		const weekSpan = new Set<string>();
		for (const monday of weekMondays) {
			for (let i = 0; i < 7; i++) weekSpan.add(addDays(monday, i));
		}
		const inBucket = (dat: string) => weekSpan.has(dat);

		const mySlots = slots.filter(s => s.id_employee === employee.id && inBucket(s.dat));
		const myPeriods = periods.filter(p => p.id_employee === employee.id && inBucket(p.dat) && p.minutes !== null);

		// La SEMAINE est l'unité légale de décompte : on calcule chacune à part, puis on additionne.
		// C'est ce qui permet à une semaine de congés d'être neutre au lieu de creuser un déficit.
		let worked = 0, assimilated = 0, excluded = 0, basis = 0, contract = 0, raw = 0, extra = 0;
		let pending = false;				// la période contient une semaine pas encore terminée
		let contingentMin = 0;			// heures sup de la période qui s'imputent sur le contingent annuel
		const proposed: OvertimeEntry[] = [];
		// Les créneaux TRAVAILLÉS de la période, pour les majorations qui ouvrent du repos.
		const premiumSlots: PremiumSlot[] = [];
		let bucketCorRate = 50;			// taux de contrepartie du dernier profil résolu de la période
		for (const monday of weekMondays) {
			const sunday = addDays(monday, 6);
			const weekSlots = mySlots.filter(s => s.dat >= monday && s.dat <= sunday);
			const weekPeriods = myPeriods.filter(p => p.dat >= monday && p.dat <= sunday);

			// Temps retenu : même cascade que le rapport de paie, JOUR PAR JOUR. En mode planning,
			// les pointages ne pilotent rien — sinon le compteur d'heures et la paie diraient deux
			// choses. Et le futur ne compte pas : une semaine qui n'a pas eu lieu n'a rien produit.
			const weekDays = new Set<string>([
				...weekSlots.map(s => s.dat),
				...weekPeriods.map(p => p.dat),
				...myValidations.filter(v => v.dat >= monday && v.dat <= sunday).map(v => v.dat),
			]);
			const reference = referenceWeekMinutes(employee, monday, anchorMonday, cycleWeeks);
			const resolved = effectiveWorkRules(reference, rules, profile);

			// FÉRIÉS CHÔMÉS de la semaine. Leurs créneaux ne sont pas du travail — l'établissement
			// était fermé — mais leurs heures restent acquises (L3133-1). On les met donc de côté
			// AVANT de compter le travaillé, et on les valorise aux heures que le planning prévoyait
			// ce jour-là (la base journalière ne sert que si aucune grille ne couvre ce jour).
			// Un férié à venir ne compte pas plus qu'un planning à venir.
			const closedHolidays = (holidayRules ? closedHolidaysInRange(holidayRules, monday, sunday) : [])
				.filter(d => !today || d.dat <= today);
			const closedDats = new Set(closedHolidays.map(d => d.dat));
			let weekHolidayMin = 0;
			for (const day of closedHolidays) {
				const planned = plannedMinutes(weekSlots.filter(s => s.dat === day.dat));
				weekHolidayMin += planned > 0 ? planned : resolved.day_base_min;
			}

			let weekWorked = 0;
			for (const dat of weekDays) {
				if (closedDats.has(dat)) continue;			// journée fermée : comptée en heures maintenues
				const daySlots = weekSlots.filter(s => s.dat === dat);
				const dayPlanned = plannedMinutes(daySlots);
				const dayElapsed = !today || dat < today ? dayPlanned
					: dat === today ? elapsedPlannedMinutes(daySlots, nowMin)
					: 0;
				const dayBadged = weekPeriods.filter(p => p.dat === dat).reduce((sum, p) => sum + (p.minutes ?? 0), 0);
				weekWorked += retainedDayMinutes(dayPlanned, dayElapsed, dayBadged,
					myValidations.find(v => v.dat === dat), source);
			}
			// Les créneaux effacés par une absence, ventilés selon le type : crédités comme du
			// travail, assimilés (neutres), ou réellement absents. Une absence À VENIR ne compte
			// pas davantage qu'un planning à venir : elle n'a pas encore eu lieu.
			let weekAssimilatedPlanned = 0, weekNotAssimilated = 0;
			for (const s of weekSlots) {
				if (!s.absent) continue;
				if (today && s.dat > today) continue;
				const typ = absenceTypOf(s.dat);
				if (typ === null) continue;
				const min = Math.max(0, (s.end_min - s.start_min) - (s.break_min ?? 0));
				if (countedTypes.has(typ)) weekWorked += min;
				else if (notAssimilatedTypes.has(typ)) weekNotAssimilated += min;
				else weekAssimilatedPlanned += min;
			}

			// JOURS de congés décomptés : c'est cette valeur, × la base journalière, qui neutralise
			// la semaine — même quand aucun créneau n'était saisi ces jours-là.
			const weekAssimilatedDays = hrCountedLeaveDays(
				approved.filter(a => {
					const t = a.typ ?? -1;
					return !countedTypes.has(t) && !notAssimilatedTypes.has(t);
				}), monday, sunday, holidays,
			).reduce((sum, d) => sum + d.days, 0);

			const outcome = weekOutcome({
				monday, worked_min: weekWorked, assimilated_min: weekAssimilatedPlanned,
				assimilated_days: weekAssimilatedDays,
				// Jours réellement travaillés : ils bornent les jours de congés valorisables.
				worked_days: new Set(weekSlots.filter(s => !s.absent && !closedDats.has(s.dat)).map(s => s.dat)).size,
				holiday_days: closedHolidays.length,
				holiday_min: weekHolidayMin,
				reference_min: reference,
			}, resolved);

			// UNE SEMAINE NON TERMINÉE NE SOLDE RIEN. La référence est un volume HEBDOMADAIRE
			// indivisible : la confronter mercredi à trois jours de travail invente un déficit de
			// deux jours (le fameux « −14 h »). Le travail déjà fait reste AFFICHÉ — c'est le solde
			// qui attend dimanche soir, quand la comparaison devient enfin honnête.
			const weekPending = !!today && sunday > today;

			worked += weekWorked;
			assimilated += weekPending ? 0 : outcome.assimilated_min;
			excluded += weekAssimilatedPlanned + weekNotAssimilated;
			basis += weekPending ? 0 : outcome.basis_min;
			contract += weekPending ? 0 : reference;
			raw += weekPending ? 0 : outcome.counter_delta_min;
			extra += weekPending ? 0 : outcome.extra_min;
			if (weekPending) pending = true;
			bucketCorRate = resolved.cor_rate;
			// Seules les heures SUPPLÉMENTAIRES et MAJORÉES consomment le contingent : ni les heures
			// complémentaires d'un temps partiel, ni les RTT, ni la récupération (cf. buildContingent,
			// qui reste l'autorité — ici on ne cherche qu'à situer le franchissement dans le temps).
			if (!weekPending) {
				contingentMin += outcome.buckets
					.filter(b => b.kind === 'overtime' && b.contingent)
					.reduce((sum, b) => sum + b.minutes, 0);
			}

			// Les créneaux réellement TRAVAILLÉS de la semaine — hors absences et hors fériés chômés :
			// c'est sur eux que se comptent les majorations qui ouvrent du repos (dimanche, 1ᵉʳ mai).
			for (const s of weekSlots) {
				if (s.absent || closedDats.has(s.dat)) continue;
				if (today && s.dat > today) continue;
				const known = holidayRules ? holidayOf(holidayRules, s.dat) : null;
				if (known && !known.open) continue;
				const day = known && holidayRules
					? holidayPremiumOf(holidayRules, s.dat, holidayRule?.rate ?? 0, holidayRule?.rest_rate ?? 0)
					: null;
				premiumSlots.push({
					dat: s.dat, weekday: isoWeekdayOf(s.dat), start_min: s.start_min, end_min: s.end_min,
					holiday: known ? day !== null : holidays.has(s.dat),
					holiday_rate: day?.rate ?? null,
					holiday_rest_rate: day?.rest_rate ?? null,
					holiday_label: day?.label ?? '',
				});
			}

			// Sans arbitrage déjà décidé, on applique la politique de l'établissement (qui choisit,
			// et par défaut quoi), corrigée par les heures que le responsable RH a explicitement
			// mises en récupération : le solde se remplit tout seul, exactement comme en paie. Un
			// arbitrage complet fourni en entrée l'emporte toujours.
			// Rien n'est acquis sur une semaine en cours : on ne crédite pas un repos compensateur
			// pour des heures supplémentaires qui peuvent encore ne pas avoir lieu.
			const restDecided = input.restDecided;
			if (!weekPending && !input.overtimeEntries && outcome.buckets.length > 0
				&& (resolved.rcr_enabled || (restDecided?.size ?? 0) > 0)) {
				proposed.push(...splitDestinations(employee.id, monday, outcome.buckets, resolved,
					b => restDecided?.get(employee.id + '|' + monday + '|' + b.rate + '|' + b.tier_kind) ?? null));
			}
		}

		const hasPlanning = mySlots.length > 0;
		// Les mouvements saisis se rangent par volet : l'écart d'un côté, la récupération de l'autre.
		const movements = adjustments.filter(a => inBucket(a.dat));
		const manual = movements
			.filter(a => hrCounterVoletOf(a.kind ?? HR_COUNTER_KIND_MANUAL) === 'balance')
			.reduce((sum, a) => sum + a.minutes, 0);
		const balance = raw + manual;
		cumulative += balance;

		// Volet récupération : les arbitrages « repos » crédités, les repos pris débités — plus les
		// mouvements de compteur explicitement rangés dans ce volet (crédit ou prise saisis à la main).
		const bucketEntries = (input.overtimeEntries ?? proposed)
			.filter(e => e.id_employee === employee.id && inBucket(e.monday));
		// Signe d'un mouvement de ce volet : POSITIF = repos acquis, NÉGATIF = repos pris.
		const rcrMovements = movements.filter(a => hrCounterVoletOf(a.kind ?? HR_COUNTER_KIND_MANUAL) === 'rcr');
		const rcrManualCredit = rcrMovements.filter(a => a.minutes > 0).reduce((sum, a) => sum + a.minutes, 0);
		const rcrManualTaken = rcrMovements.filter(a => a.minutes < 0).reduce((sum, a) => sum - a.minutes, 0);
		const takenMin = (input.rcrTaken ?? []).filter(t => inBucket(t.dat)).reduce((sum, t) => sum + t.minutes, 0)
			+ rcrManualTaken;
		const rcr = rcrBalanceOf(bucketEntries, takenMin);

		// REPOS DES MAJORATIONS — « le dimanche, +17 % et 100 % du temps en récupération ». Il vient
		// s'ajouter au stock, sans rien retirer de la paie : les deux contreparties se cumulent.
		const premiumRest = premiumRestMinutesOf(premiumTotalsOf(premiumSlots, rules.premiums));

		// CONTREPARTIE OBLIGATOIRE EN REPOS née DANS CETTE PÉRIODE : ce que les heures de la période
		// ont fait franchir au contingent annuel, et rien d'autre. Compter l'excédent total à chaque
		// bucket le répéterait de mois en mois.
		const contingentCap = Math.round(resolvedProfile.contingent_year_h * 60);
		const overBefore = Math.max(0, contingentUsed - contingentCap);
		contingentUsed += contingentMin;
		const corMin = Math.round((Math.max(0, contingentUsed - contingentCap) - overBefore) * bucketCorRate / 100);

		return {
			key: bucket.key,
			label: bucket.label,
			from: bucket.from,
			to: bucket.to,
			worked_min: worked,
			assimilated_min: assimilated,
			basis_min: basis,
			excluded_absence_min: excluded,
			contract_min: contract,
			extra_min: extra,
			raw_balance: raw,
			manual_min: manual,
			balance,
			cumulative,
			planned: hasPlanning,
			pending,
			rcr_credited_min: rcr.credited_min + rcrManualCredit,
			premium_rest_min: premiumRest,
			cor_min: corMin,
			rcr_taken_min: rcr.taken_min,
			// LE SOLDE À POSER : les trois origines moins ce qui a déjà été pris. Un seul chiffre,
			// parce qu'un salarié ne pose pas « 2 h de RCR et 1 h de COR » — il pose 3 h.
			rcr_balance_min: rcr.balance_min + rcrManualCredit + premiumRest + corMin,
		};
	});
}

/// « +4h30 » / « −2h15 » / « 0h » — libellé signé d'un solde en minutes.
export function counterLabel(minutes: number): string {
	if (minutes === 0) return '0h';
	const sign = minutes > 0 ? '+' : '−';
	const abs = Math.abs(minutes);
	const h = Math.floor(abs / 60);
	const m = abs % 60;
	return sign + h + 'h' + (m > 0 ? (m < 10 ? '0' + m : '' + m) : '');
}
